import L from "leaflet";
import * as turf from "@turf/turf";

export const crossSectionTool = {
  id: "cross-section",
  name: "Cross Sections",
  init(app) { this._rt = this._rt ?? createRuntime(app); },
  destroy() { this._rt?.destroy?.(); this._rt = null; },
  renderPanel(app, containerEl) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.mount(containerEl);
  },
  serialize() { return this._rt?.snapshot?.() ?? null; },
  restore(app, data) {
    if (!this._rt) this._rt = createRuntime(app);
    this._rt.restore?.(data);
  },
};

// ── Constants ──────────────────────────────────────────────────────────────────
const FT_PER_M = 3.28084;
const PROFILE_SAMPLES = 256;
const SURFACE_COLORS = [
  "#38bdf8", "#f97316", "#22c55e", "#a855f7",
  "#ef4444", "#facc15", "#14b8a6", "#fb7185",
];

// ── Helpers ────────────────────────────────────────────────────────────────────
function esc(v) {
  return String(v ?? "").replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

let _seq = 1;
function uid() { return `xs_${Date.now().toString(36)}_${(_seq++).toString(36)}`; }

function formatStation(chainageFt) {
  const c = Math.round(Math.abs(chainageFt));
  return `${Math.floor(c / 100)}+${String(c % 100).padStart(2, "0")}`;
}

function haversineM(lat1, lon1, lat2, lon2) {
  const R = 6371008.8, r = Math.PI / 180;
  const dLat = (lat2 - lat1) * r, dLon = (lon2 - lon1) * r;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * r) * Math.cos(lat2 * r) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

// ── Runtime ────────────────────────────────────────────────────────────────────
function createRuntime(app) {
  const map = app.map.instance;

  // Custom pane above surface panes (810+) so sections are always on top
  let xsPane;
  try { xsPane = map.createPane("xs-overlay-pane"); }
  catch { xsPane = map.getPane("xs-overlay-pane"); }
  xsPane.style.zIndex = "900";
  xsPane.style.pointerEvents = "none"; // don't intercept clicks — slip markers and other tools must stay clickable

  // Inject cursor stylesheet — overrides Leaflet's per-element hover cursor
  let cursorStyleEl = null;
  function setCrosshairCursor(on) {
    if (on && !cursorStyleEl) {
      cursorStyleEl = document.createElement("style");
      cursorStyleEl.textContent =
        `.leaflet-container,.leaflet-interactive{cursor:crosshair!important}`;
      document.head.appendChild(cursorStyleEl);
    } else if (!on && cursorStyleEl) {
      cursorStyleEl.remove();
      cursorStyleEl = null;
    }
  }

  const root = L.featureGroup().addTo(map);

  // ── State ──────────────────────────────────────────────────────────────────
  let container = null;
  let activeMarinaCode = "";
  let catalog = [];           // [{ id, name, centerline, spacing, defaultHalfWidth, surfaceRasterPaths, sections }]
  let activeSetId = null;     // currently open section set
  let selectedIds = new Set();// selected section IDs (for table multi-select)
  let drawMode = null;        // null | "single" | "centerline"
  let drawPoints = [];        // lat/lon clicks during draw
  let drawPolyline = null;    // L.polyline preview while drawing centerline
  let profileCache = new Map();// "rasterPath::sectionId" → { distances, values }
  let profileFetching = new Set();
  let availSurfaces = [];     // [{ rasterPath, name }] from /api/surfaces/catalog
  let hoverMarker = null;

  // ── Map layer refs ──────────────────────────────────────────────────────────
  const layersBySectionId = new Map();  // id → { line, labelMarker }
  let centerlineLayer = null;

  // ── Chart zoom state ───────────────────────────────────────────────────────
  let chartZoom = null;      // null = auto-fit | { xMin, xMax, yMin, yMax }
  let lastChartExtents = null; // { vxMin, vxMax, vyMin, vyMax, ML, MR, MT, MB, H, cW, cH }
  let chartDrag = null;      // { startX, startY, zoom } during drag
  let verticalExaggeration = 1; // 1 | 2 | 5 | 10
  let chartResizeObserver = null;

  // ── Catalog persistence ────────────────────────────────────────────────────
  async function loadCatalog() {
    if (!activeMarinaCode) { catalog = []; return; }
    try {
      const r = await fetch(`/api/xsection/catalog?marina=${encodeURIComponent(activeMarinaCode)}`);
      const d = await r.json();
      catalog = Array.isArray(d?.catalog) ? d.catalog : [];
    } catch { catalog = []; }
  }

  async function saveCatalog() {
    if (!activeMarinaCode) return;
    try {
      await fetch("/api/xsection/catalog", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ marina: activeMarinaCode, catalog }),
      });
    } catch { /* non-fatal */ }
  }

  // ── Active set helpers ──────────────────────────────────────────────────────
  function activeSet() { return catalog.find(s => s.id === activeSetId) ?? null; }

  function newSet(name = "Section Set") {
    return {
      id: uid(), name,
      centerline: [],
      spacing: 50,
      defaultHalfWidth: 100,
      surfaceRasterPaths: [],
      sections: [],
    };
  }

  // ── Surface list ────────────────────────────────────────────────────────────
  async function loadAvailSurfaces() {
    if (!activeMarinaCode) { availSurfaces = []; return; }
    try {
      const r = await fetch(`/api/surfaces/catalog?marina=${encodeURIComponent(activeMarinaCode)}`);
      const d = await r.json();
      availSurfaces = (d?.catalog ?? [])
        .filter(s => s.rasterReady && s.rasterPath)
        .map(s => ({ rasterPath: s.rasterPath, name: s.name || s.id }));
    } catch { availSurfaces = []; }
  }

  // ── Station / section geometry ──────────────────────────────────────────────
  function generateSectionsFromCenterline(latlngs, spacingFt, halfWidthFt) {
    const spacingM = spacingFt / FT_PER_M;
    const halfM = halfWidthFt / FT_PER_M;
    const line = turf.lineString(latlngs.map(([lat, lon]) => [lon, lat]));
    const totalM = turf.length(line, { units: "meters" });
    if (totalM < 1) return [];

    const out = [];
    let chainageFt = 0;
    let chainageM = 0;

    while (chainageM <= totalM + 0.001) {
      const along = turf.along(line, chainageM, { units: "meters" });
      const [lon, lat] = along.geometry.coordinates;

      // Bearing at this point along line
      const tNext = Math.min(chainageM + 0.1, totalM);
      const pNext = turf.along(line, tNext, { units: "meters" });
      const fwdBearing = turf.bearing(along, pNext);
      const perpBearing = fwdBearing + 90;

      const pLeft = turf.destination(along, halfM, perpBearing - 180, { units: "meters" });
      const pRight = turf.destination(along, halfM, perpBearing, { units: "meters" });

      out.push({
        id: uid(),
        station: formatStation(chainageFt),
        chainage: chainageFt,
        halfWidth: halfWidthFt,
        start: [pLeft.geometry.coordinates[1], pLeft.geometry.coordinates[0]],
        end: [pRight.geometry.coordinates[1], pRight.geometry.coordinates[0]],
      });

      chainageFt += spacingFt;
      chainageM += spacingM;
    }
    return out;
  }

  function recomputeSectionEndpoints(section, centerlinePt, fwdBearing) {
    const halfM = section.halfWidth / FT_PER_M;
    const perpBearing = fwdBearing + 90;
    const center = turf.point([centerlinePt[1], centerlinePt[0]]);
    const pLeft = turf.destination(center, halfM, perpBearing - 180, { units: "meters" });
    const pRight = turf.destination(center, halfM, perpBearing, { units: "meters" });
    section.start = [pLeft.geometry.coordinates[1], pLeft.geometry.coordinates[0]];
    section.end = [pRight.geometry.coordinates[1], pRight.geometry.coordinates[0]];
  }

  // ── Map rendering ───────────────────────────────────────────────────────────
  const SECTION_STYLE = { color: "#38bdf8", weight: 2, opacity: 0.8 };
  const SECTION_SEL_STYLE = { color: "#f97316", weight: 3, opacity: 1 };
  const CL_STYLE = { color: "#94a3b8", weight: 2, dashArray: "6 4", opacity: 0.7 };

  function drawSectionOnMap(section) {
    const existing = layersBySectionId.get(section.id);
    if (existing) {
      existing.line.setLatLngs([section.start, section.end]);
      existing.labelMarker.setLatLng(section.start);
      return;
    }
    const line = L.polyline([section.start, section.end], {
      ...SECTION_STYLE, pane: "xs-overlay-pane", interactive: false,
    }).addTo(map);
    const icon = L.divIcon({
      className: "",
      html: `<span style="font:10px/1 monospace;color:#94a3b8;white-space:nowrap;text-shadow:0 0 3px #0f172a;pointer-events:none">${esc(section.station)}</span>`,
      iconAnchor: [0, 0],
    });
    const labelMarker = L.marker(section.start, {
      icon, interactive: false, pane: "xs-overlay-pane",
    }).addTo(map);
    layersBySectionId.set(section.id, { line, labelMarker });
    applySelectionStyle(section.id);
  }

  function applySelectionStyle(sectionId) {
    const layers = layersBySectionId.get(sectionId);
    if (!layers) return;
    layers.line.setStyle(selectedIds.has(sectionId) ? SECTION_SEL_STYLE : SECTION_STYLE);
  }

  function removeSectionFromMap(sectionId) {
    const layers = layersBySectionId.get(sectionId);
    if (!layers) return;
    layers.line.remove();
    layers.labelMarker.remove();
    layersBySectionId.delete(sectionId);
  }

  function redrawAllSections() {
    // Remove stale
    const set = activeSet();
    const sectionIds = new Set((set?.sections ?? []).map(s => s.id));
    for (const id of layersBySectionId.keys()) {
      if (!sectionIds.has(id)) removeSectionFromMap(id);
    }
    // Draw/update
    for (const section of (set?.sections ?? [])) {
      drawSectionOnMap(section);
    }
    // Centerline
    if (centerlineLayer) { centerlineLayer.remove(); centerlineLayer = null; }
    if (set?.centerline?.length > 1) {
      centerlineLayer = L.polyline(set.centerline, { ...CL_STYLE, pane: "xs-overlay-pane" }).addTo(map);
    }
  }

  // ── Selection ──────────────────────────────────────────────────────────────
  function selectSection(id, ctrl, shift) {
    const set = activeSet();
    if (!set) return;
    const ids = set.sections.map(s => s.id);
    const idx = ids.indexOf(id);
    if (idx === -1) return;

    if (shift && selectedIds.size > 0) {
      const lastSel = ids.findLastIndex(i => selectedIds.has(i));
      const [lo, hi] = [Math.min(lastSel, idx), Math.max(lastSel, idx)];
      if (!ctrl) selectedIds.clear();
      for (let i = lo; i <= hi; i++) selectedIds.add(ids[i]);
    } else if (ctrl) {
      if (selectedIds.has(id)) selectedIds.delete(id);
      else selectedIds.add(id);
    } else {
      selectedIds.clear();
      selectedIds.add(id);
    }
    for (const sid of ids) applySelectionStyle(sid);
  }

  function selectAdjacentSection(delta) {
    const set = activeSet();
    if (!set?.sections?.length) return;
    const ids = set.sections.map(s => s.id);
    const firstSel = ids.find(id => selectedIds.has(id));
    const curIdx = firstSel ? ids.indexOf(firstSel) : -1;
    const nextIdx = curIdx === -1
      ? (delta > 0 ? 0 : ids.length - 1)
      : (curIdx + delta + ids.length) % ids.length;
    selectedIds.clear();
    selectedIds.add(ids[nextIdx]);
    for (const id of ids) applySelectionStyle(id);
    renderTable();
    chartZoom = null;
    fetchMissingProfiles();
    renderChart();
    // Scroll within the table container only — don't let it bubble to the page
    const tableWrap = container?.querySelector?.("#xs-table-wrap");
    const tr = tableWrap?.querySelector?.(`tr[data-id="${esc(ids[nextIdx])}"]`);
    if (tr && tableWrap) {
      const trTop = tr.offsetTop;
      const trBot = trTop + tr.offsetHeight;
      if (trTop < tableWrap.scrollTop) tableWrap.scrollTop = trTop;
      else if (trBot > tableWrap.scrollTop + tableWrap.clientHeight) tableWrap.scrollTop = trBot - tableWrap.clientHeight;
    }
  }

  // ── Profile fetching ───────────────────────────────────────────────────────
  async function fetchProfile(rasterPath, section) {
    const key = `${rasterPath}::${section.id}`;
    if (profileCache.has(key) || profileFetching.has(key)) return;
    profileFetching.add(key);
    try {
      const r = await fetch("/api/xsection/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          url: rasterPath,
          start: section.start,
          end: section.end,
          samples: PROFILE_SAMPLES,
        }),
      });
      const d = await r.json();
      if (d.ok) profileCache.set(key, { distances: d.distances, values: d.values });
      else profileCache.set(key, { error: true, distances: [], values: [] });
    } catch {
      profileCache.set(key, { error: true, distances: [], values: [] });
    } finally {
      profileFetching.delete(key);
    }
  }

  async function fetchMissingProfiles() {
    const set = activeSet();
    if (!set || !selectedIds.size) return;
    const sections = set.sections.filter(s => selectedIds.has(s.id));
    const fetches = [];
    for (const rp of set.surfaceRasterPaths) {
      for (const sec of sections) {
        const key = `${rp}::${sec.id}`;
        if (!profileCache.has(key) && !profileFetching.has(key)) {
          fetches.push(fetchProfile(rp, sec));
        }
      }
    }
    if (!fetches.length) return;
    await Promise.all(fetches);
    renderChart();
  }

  // ── SVG Chart ─────────────────────────────────────────────────────────────
  function renderChart() {
    const chartEl = container?.querySelector?.("#xs-chart");
    if (!chartEl) return;
    const set = activeSet();
    if (!set || !selectedIds.size) {
      chartEl.innerHTML = `<div class="small muted" style="padding:12px">Select a section to view profile.</div>`;
      return;
    }

    if (!set.surfaceRasterPaths.length) {
      chartEl.innerHTML = `<div class="small muted" style="padding:12px">Add a surface to view profile.</div>`;
      return;
    }

    // Collect all profile data for selected sections × surfaces
    const lines = []; // [{ label, color, distances[], values[], sectionId, loading }]
    let surfIdx = 0;
    for (const rp of set.surfaceRasterPaths) {
      const color = SURFACE_COLORS[surfIdx % SURFACE_COLORS.length];
      const surfName = availSurfaces.find(s => s.rasterPath === rp)?.name ?? rp.split("/").pop();
      for (const sId of selectedIds) {
        const sec = set.sections.find(s => s.id === sId);
        if (!sec) continue;
        const key = `${rp}::${sId}`;
        const prof = profileCache.get(key);
        if (!prof) {
          // Not yet cached (in-flight or pending) — show loading placeholder
          lines.push({ label: `${surfName} / ${sec.station}`, color, distances: [], values: [], sectionId: sId, loading: true });
        } else {
          // Cached (includes error sentinel which has empty arrays)
          lines.push({ label: `${surfName} / ${sec.station}`, color, distances: prof.distances, values: prof.values, sectionId: sId, loading: false });
        }
      }
      surfIdx++;
    }

    if (lines.every(l => l.loading)) {
      chartEl.innerHTML = `<div class="small muted" style="padding:12px">Loading profile…</div>`;
      return;
    }

    // Compute full data extents
    let xMax = 0, yMin = Infinity, yMax = -Infinity;
    for (const ln of lines) {
      if (ln.loading) continue;
      for (let i = 0; i < ln.distances.length; i++) {
        if (ln.distances[i] > xMax) xMax = ln.distances[i];
        const v = ln.values[i];
        if (v !== null && Number.isFinite(v)) {
          if (v < yMin) yMin = v;
          if (v > yMax) yMax = v;
        }
      }
    }
    if (!Number.isFinite(yMin)) { yMin = -10; yMax = 10; }
    const xPad = xMax * 0.02 || 1;
    const yPad = (yMax - yMin) * 0.08 || 1;
    const dataXMin = 0, dataXMax = xMax + xPad;
    const dataYMin = yMin - yPad, dataYMax = yMax + yPad;

    // chartZoom stores raw Y (VE=1 space); VE is applied on top
    let vxMin = dataXMin, vxMax = dataXMax;
    let rawYMin = dataYMin, rawYMax = dataYMax;
    if (chartZoom) { vxMin = chartZoom.xMin; vxMax = chartZoom.xMax; rawYMin = chartZoom.yMin; rawYMax = chartZoom.yMax; }

    // Apply VE to raw Y range (compress around midpoint → features appear taller)
    const yCenter = (rawYMin + rawYMax) / 2;
    const yHalf = (rawYMax - rawYMin) / 2;
    const vyMin = yCenter - yHalf / verticalExaggeration;
    const vyMax = yCenter + yHalf / verticalExaggeration;

    const W = chartEl.clientWidth || 600;
    const H = 220;
    const ML = 54, MR = 12, MT = 10, MB = 36;
    const cW = W - ML - MR, cH = H - MT - MB;

    // Store extents for event handlers (persistent across re-renders)
    lastChartExtents = { vxMin, vxMax, vyMin, vyMax, rawYMin, rawYMax, dataXMin, dataXMax, dataYMin, dataYMax, ML, MR, MT, MB, H, cW, cH, W };

    function sx(d) { return ML + ((d - vxMin) / (vxMax - vxMin)) * cW; }
    function sy(v) { return MT + (1 - (v - vyMin) / (vyMax - vyMin)) * cH; }

    // Show/hide zoom reset button
    const resetBtn = container?.querySelector?.("#xs-zoom-reset");
    if (resetBtn) resetBtn.style.display = chartZoom ? "" : "none";

    // Gridlines
    const yTicks = 5;
    let gridLines = "";
    let yLabels = "";
    for (let i = 0; i <= yTicks; i++) {
      const v = vyMin + (i / yTicks) * (vyMax - vyMin);
      const y = sy(v);
      gridLines += `<line x1="${ML}" y1="${y}" x2="${W - MR}" y2="${y}" stroke="rgba(148,163,184,.18)" stroke-width="1"/>`;
      yLabels += `<text x="${ML - 4}" y="${y + 4}" text-anchor="end" font-size="10" fill="#64748b">${v.toFixed(1)}</text>`;
    }
    const xTicks = 5;
    let xLabels = "";
    for (let i = 0; i <= xTicks; i++) {
      const d = vxMin + (i / xTicks) * (vxMax - vxMin);
      const x = sx(d);
      gridLines += `<line x1="${x}" y1="${MT}" x2="${x}" y2="${H - MB}" stroke="rgba(148,163,184,.12)" stroke-width="1"/>`;
      xLabels += `<text x="${x}" y="${H - MB + 14}" text-anchor="middle" font-size="10" fill="#64748b">${d.toFixed(0)}</text>`;
    }

    // Data paths — clip to visible x range
    let paths = "";
    for (const ln of lines) {
      if (ln.loading || !ln.distances.length) continue;
      let d = "";
      let penUp = true;
      for (let i = 0; i < ln.distances.length; i++) {
        const v = ln.values[i];
        if (v === null || !Number.isFinite(v)) { penUp = true; continue; }
        const x = sx(ln.distances[i]);
        const y = sy(v);
        if (penUp) { d += `${d ? " " : ""}M${x},${y}`; penUp = false; }
        else { d += ` L${x},${y}`; }
      }
      if (d) paths += `<path d="${d}" fill="none" stroke="${ln.color}" stroke-width="1.5" stroke-linejoin="round"/>`;
    }

    // Legend
    let legend = "";
    const linesWithData = lines.filter(l => !l.loading);
    linesWithData.forEach((ln, i) => {
      const lx = ML + (i % 3) * Math.floor(cW / 3);
      const ly = H - MB + 28;
      legend += `<line x1="${lx}" y1="${ly}" x2="${lx + 14}" y2="${ly}" stroke="${ln.color}" stroke-width="2"/>`;
      legend += `<text x="${lx + 18}" y="${ly + 4}" font-size="9" fill="#94a3b8">${esc(ln.label)}</text>`;
    });

    // Hover overlay (invisible rect for mousemove)
    const svgId = `xs-svg-${Date.now()}`;
    chartEl.innerHTML = `
      <svg id="${svgId}" width="${W}" height="${H}" style="display:block;overflow:visible">
        <defs>
          <clipPath id="${svgId}-clip">
            <rect x="${ML}" y="${MT}" width="${cW}" height="${cH}"/>
          </clipPath>
        </defs>
        ${gridLines}
        <line x1="${ML}" y1="${MT}" x2="${ML}" y2="${H - MB}" stroke="#334155" stroke-width="1"/>
        <line x1="${ML}" y1="${H - MB}" x2="${W - MR}" y2="${H - MB}" stroke="#334155" stroke-width="1"/>
        ${yLabels}
        <text x="${ML - 32}" y="${MT + cH / 2}" text-anchor="middle" font-size="10" fill="#64748b"
              transform="rotate(-90,${ML - 32},${MT + cH / 2})">Elev (ft)</text>
        ${xLabels}
        <text x="${ML + cW / 2}" y="${H - 2}" text-anchor="middle" font-size="10" fill="#64748b">Distance (ft)</text>
        <g clip-path="url(#${svgId}-clip)">${paths}</g>
        ${legend}
        <line id="xs-crosshair" x1="0" y1="${MT}" x2="0" y2="${H - MB}" stroke="rgba(255,255,255,.3)" stroke-width="1" display="none"/>
        <circle id="xs-dot" r="4" fill="white" stroke="#0f172a" stroke-width="1.5" display="none"/>
        <rect id="xs-hover-zone" x="${ML}" y="${MT}" width="${cW}" height="${cH}" fill="transparent" style="cursor:crosshair"/>
      </svg>
      <div id="xs-tooltip" style="position:absolute;pointer-events:none;background:#1e293b;border:1px solid #334155;border-radius:4px;padding:4px 8px;font-size:11px;color:#e2e8f0;display:none;white-space:nowrap;"></div>`;

    // Hover interaction
    const svgEl = chartEl.querySelector(`#${svgId}`);
    const hoverZone = chartEl.querySelector("#xs-hover-zone");
    const crosshair = chartEl.querySelector("#xs-crosshair");
    const dot = chartEl.querySelector("#xs-dot");
    const tooltip = chartEl.querySelector("#xs-tooltip");
    if (!hoverZone) return;

    hoverZone.addEventListener("mousemove", (e) => {
      const rect = svgEl.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      if (mouseX < ML || mouseX > W - MR) return;
      const distFt = vxMin + ((mouseX - ML) / cW) * (vxMax - vxMin);
      // Find nearest sample across all lines
      let bestVal = null, bestDist = Infinity, bestLn = null;
      for (const ln of linesWithData) {
        for (let i = 0; i < ln.distances.length; i++) {
          const d = Math.abs(ln.distances[i] - distFt);
          if (d < bestDist && ln.values[i] !== null) {
            bestDist = d; bestVal = ln.values[i]; bestLn = ln;
          }
        }
      }
      crosshair.setAttribute("x1", mouseX);
      crosshair.setAttribute("x2", mouseX);
      crosshair.setAttribute("display", "");
      if (bestVal !== null && bestLn) {
        const dy = sy(bestVal);
        dot.setAttribute("cx", mouseX);
        dot.setAttribute("cy", dy);
        dot.setAttribute("stroke", bestLn.color);
        dot.setAttribute("display", "");
        tooltip.style.display = "block";
        tooltip.style.left = `${mouseX + 10}px`;
        tooltip.style.top = `${dy - 16}px`;
        tooltip.textContent = `${distFt.toFixed(1)} ft  |  ${bestVal.toFixed(2)} ft`;
        // Move map marker
        if (hoverMarker && bestLn.sectionId) {
          const sec = set.sections.find(s => s.id === bestLn.sectionId);
          if (sec) {
            const t = distFt / (bestLn.distances.at(-1) || 1);
            const lat = sec.start[0] + (sec.end[0] - sec.start[0]) * t;
            const lon = sec.start[1] + (sec.end[1] - sec.start[1]) * t;
            hoverMarker.setLatLng([lat, lon]).setOpacity(1);
          }
        }
      }
    });
    hoverZone.addEventListener("mouseleave", () => {
      crosshair.setAttribute("display", "none");
      dot.setAttribute("display", "none");
      tooltip.style.display = "none";
      if (hoverMarker) hoverMarker.setOpacity(0);
    });
  }

  // ── CSV Export ─────────────────────────────────────────────────────────────
  function exportCSV() {
    const set = activeSet();
    if (!set) return;
    const sections = selectedIds.size
      ? set.sections.filter(s => selectedIds.has(s.id))
      : set.sections;
    const rows = ["station,distance_ft,lat,lon," + set.surfaceRasterPaths.map((rp, i) =>
      `elev_${i + 1}_ft`).join(",")];
    for (const sec of sections) {
      const profiles = set.surfaceRasterPaths.map(rp =>
        profileCache.get(`${rp}::${sec.id}`) ?? null);
      const nSamples = profiles.find(p => p)?.distances?.length ?? 0;
      for (let i = 0; i < nSamples; i++) {
        const distFt = profiles.find(p => p)?.distances?.[i] ?? 0;
        const t = nSamples > 1 ? i / (nSamples - 1) : 0;
        const lat = (sec.start[0] + (sec.end[0] - sec.start[0]) * t).toFixed(7);
        const lon = (sec.start[1] + (sec.end[1] - sec.start[1]) * t).toFixed(7);
        const elevs = profiles.map(p => {
          const v = p?.values?.[i];
          return (v !== null && Number.isFinite(v)) ? v.toFixed(3) : "";
        });
        rows.push(`${sec.station},${distFt.toFixed(2)},${lat},${lon},${elevs.join(",")}`);
      }
    }
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(blob),
      download: `${set.name.replace(/\s+/g, "_")}_xsections.csv`,
    });
    a.click(); URL.revokeObjectURL(a.href);
  }

  // ── Draw modes ─────────────────────────────────────────────────────────────
  function startDraw(mode) {
    stopDraw();
    drawMode = mode;
    drawPoints = [];
    setCrosshairCursor(true);
    map.on("click", onMapClick);
    map.on("dblclick", onMapDblClick);
    renderPanel();
  }

  function stopDraw() {
    drawMode = null;
    drawPoints = [];
    setCrosshairCursor(false);
    map.off("click", onMapClick);
    map.off("dblclick", onMapDblClick);
    if (drawPolyline) { drawPolyline.remove(); drawPolyline = null; }
    renderPanel();
  }

  function onMapClick(e) {
    const { lat, lng } = e.latlng;
    if (drawMode === "single") {
      drawPoints.push([lat, lng]);
      if (drawPoints.length === 2) {
        commitSingleSection(drawPoints[0], drawPoints[1]);
        stopDraw();
      }
    } else if (drawMode === "centerline") {
      // Prevent double-click from adding two points
      if (e.originalEvent._isDoubleClick) return;
      drawPoints.push([lat, lng]);
      if (!drawPolyline) {
        drawPolyline = L.polyline(drawPoints, { ...CL_STYLE, pane: "xs-overlay-pane" }).addTo(map);
      } else {
        drawPolyline.setLatLngs(drawPoints);
      }
    }
  }

  function onMapDblClick(e) {
    L.DomEvent.stop(e); // prevent map zoom
    if (drawMode === "centerline") {
      // The dblclick also fired a click event which already added the final point — remove it
      if (drawPoints.length > 0) drawPoints.pop();
      if (drawPoints.length >= 2) {
        commitCenterlineSection(drawPoints.slice()); // slice before stopDraw clears the array
      }
      stopDraw();
    }
  }

  function commitSingleSection(start, end) {
    const set = ensureActiveSet();
    const distFt = haversineM(start[0], start[1], end[0], end[1]) * FT_PER_M;
    set.centerline = [];
    set.sections.push({
      id: uid(),
      station: "0+00",
      chainage: 0,
      halfWidth: set.defaultHalfWidth,
      start, end,
    });
    selectedIds.clear();
    selectedIds.add(set.sections.at(-1).id);
    afterSetChange();
  }

  function commitCenterlineSection(latlngs) {
    const set = ensureActiveSet();
    set.centerline = latlngs;
    // Show config form, don't generate yet
    renderPanel();
    showConfigForm();
  }

  function generateFromCenterline() {
    const set = activeSet();
    if (!set || !set.centerline.length) return;
    set.sections = generateSectionsFromCenterline(set.centerline, set.spacing, set.defaultHalfWidth);
    selectedIds.clear();
    afterSetChange();
  }

  function ensureActiveSet() {
    if (!activeSetId) {
      const s = newSet();
      catalog.push(s);
      activeSetId = s.id;
    }
    return activeSet();
  }

  function afterSetChange() {
    redrawAllSections();
    saveCatalog();
    fetchMissingProfiles();
    renderPanel();
    renderChart();
  }

  // ── Panel HTML ─────────────────────────────────────────────────────────────
  function showConfigForm() {
    const set = activeSet();
    if (!set) return;
    const formEl = container?.querySelector?.("#xs-config-form");
    if (formEl) formEl.style.display = "";
  }

  function renderPanel() {
    if (!container) return;
    const set = activeSet();

    // Set selector row
    const setSelect = container.querySelector("#xs-set-select");
    if (setSelect) {
      const cur = setSelect.value;
      setSelect.innerHTML = `<option value="">— New set —</option>` +
        catalog.map(s => `<option value="${esc(s.id)}"${s.id === activeSetId ? " selected" : ""}>${esc(s.name)}</option>`).join("");
      if (!setSelect.value && activeSetId) setSelect.value = activeSetId;
    }

    // Config form visibility
    const configForm = container.querySelector("#xs-config-form");
    if (configForm) {
      configForm.style.display = set?.centerline?.length ? "" : "none";
      if (set) {
        const spEl = configForm.querySelector("#xs-spacing");
        const hwEl = configForm.querySelector("#xs-halfwidth");
        const nameEl = configForm.querySelector("#xs-setname");
        if (spEl) spEl.value = set.spacing;
        if (hwEl) hwEl.value = set.defaultHalfWidth;
        if (nameEl) nameEl.value = set.name;
      }
    }

    // Draw mode indicators
    const singleBtn = container.querySelector("#xs-draw-single");
    const clBtn = container.querySelector("#xs-draw-cl");
    if (singleBtn) singleBtn.classList.toggle("btn-active", drawMode === "single");
    if (clBtn) clBtn.classList.toggle("btn-active", drawMode === "centerline");

    // Surface list
    renderSurfaceList();

    // Section table
    renderTable();

    // Chart
    renderChart();
  }

  function renderSurfaceList() {
    const set = activeSet();
    const el = container?.querySelector?.("#xs-surfaces-list");
    if (!el) return;
    if (!set) { el.innerHTML = `<div class="small muted">Open a section set first.</div>`; return; }

    el.innerHTML = availSurfaces.map(s => {
      const active = set.surfaceRasterPaths.includes(s.rasterPath);
      return `<label class="xs-surf-item" style="display:flex;align-items:center;gap:6px;cursor:pointer;padding:2px 0;">
        <input type="checkbox" data-rp="${esc(s.rasterPath)}"${active ? " checked" : ""}/>
        <span class="small">${esc(s.name)}</span>
      </label>`;
    }).join("") || `<div class="small muted">No raster surfaces in catalog.</div>`;

    el.querySelectorAll("input[type=checkbox]").forEach(cb => {
      cb.onchange = () => {
        if (!set) return;
        const rp = cb.dataset.rp;
        if (cb.checked) { if (!set.surfaceRasterPaths.includes(rp)) set.surfaceRasterPaths.push(rp); }
        else { set.surfaceRasterPaths = set.surfaceRasterPaths.filter(r => r !== rp); }
        profileCache.clear();
        saveCatalog();
        fetchMissingProfiles();
        renderChart();
      };
    });
  }

  function renderTable() {
    const set = activeSet();
    const el = container?.querySelector?.("#xs-table-body");
    if (!el) return;
    if (!set?.sections?.length) {
      el.innerHTML = `<tr><td colspan="3" class="small muted" style="padding:8px">No sections.</td></tr>`;
      return;
    }
    el.innerHTML = set.sections.map((sec, idx) => {
      const sel = selectedIds.has(sec.id);
      const lenFt = (haversineM(sec.start[0], sec.start[1], sec.end[0], sec.end[1]) * FT_PER_M).toFixed(0);
      return `<tr data-id="${esc(sec.id)}" class="${sel ? "xs-row-sel" : ""}" style="cursor:pointer">
        <td class="small" style="padding:2px 6px;">${esc(sec.station)}</td>
        <td class="small" style="padding:2px 6px;">${lenFt} ft</td>
        <td style="padding:2px 6px;">
          <input class="xs-hw" data-id="${esc(sec.id)}" type="number" value="${sec.halfWidth}"
            style="width:52px;font-size:11px;" title="Half-width (ft)"/>
        </td>
      </tr>`;
    }).join("");

    // Row click for selection
    el.querySelectorAll("tr[data-id]").forEach(tr => {
      tr.onclick = (e) => {
        if (e.target.classList.contains("xs-hw")) return;
        selectSection(tr.dataset.id, e.ctrlKey || e.metaKey, e.shiftKey);
        chartZoom = null;
        renderTable();
        fetchMissingProfiles();
        renderChart();
      };
    });

    // Half-width overrides
    el.querySelectorAll("input.xs-hw").forEach(inp => {
      inp.onchange = () => {
        const sec = set?.sections?.find(s => s.id === inp.dataset.id);
        if (!sec) return;
        sec.halfWidth = Math.max(1, Number(inp.value) || set.defaultHalfWidth);
        if (set.centerline.length > 1) {
          // Recompute endpoints from centerline
          const line = turf.lineString(set.centerline.map(([lt, ln]) => [ln, lt]));
          const chainageM = sec.chainage / FT_PER_M;
          const along = turf.along(line, chainageM, { units: "meters" });
          const tNext = Math.min(chainageM + 0.1, turf.length(line, { units: "meters" }));
          const pNext = turf.along(line, tNext, { units: "meters" });
          const fwdBearing = turf.bearing(along, pNext);
          recomputeSectionEndpoints(sec, [along.geometry.coordinates[1], along.geometry.coordinates[0]], fwdBearing);
        }
        // Invalidate cache for this section
        for (const rp of set.surfaceRasterPaths) profileCache.delete(`${rp}::${sec.id}`);
        redrawAllSections();
        saveCatalog();
        fetchMissingProfiles();
        renderChart();
      };
    });
  }

  // ── Mount / unmount ─────────────────────────────────────────────────────────
  function mount(el) {
    container = el;
    container.style.position = "relative";
    container.innerHTML = `
<div style="display:flex;flex-direction:column;gap:8px;height:100%;overflow:hidden;">

  <!-- Set selector -->
  <div class="row" style="gap:6px;align-items:center;">
    <select id="xs-set-select" style="flex:1;min-width:0;"></select>
    <button id="xs-set-new" class="btn btn-sm" title="New section set">+</button>
    <button id="xs-set-delete" class="btn btn-sm btn-danger" title="Delete set">✕</button>
  </div>

  <!-- Name (shown when set active) -->
  <div id="xs-name-row" style="display:flex;gap:6px;">
    <input id="xs-setname" type="text" placeholder="Set name…" style="flex:1;min-width:0;font-size:12px;"/>
    <button id="xs-set-rename" class="btn btn-sm">Rename</button>
  </div>

  <!-- Draw tools -->
  <div class="row" style="gap:6px;">
    <button id="xs-draw-single" class="btn btn-sm" title="Draw a single cross-section (2 clicks)">Single</button>
    <button id="xs-draw-cl" class="btn btn-sm" title="Draw centerline (click vertices, dbl-click to finish)">Centerline</button>
    <button id="xs-stop-draw" class="btn btn-sm" style="display:none">Cancel</button>
  </div>

  <!-- Centerline config form -->
  <div id="xs-config-form" style="display:none;background:rgba(15,23,42,.6);border:1px solid #1e293b;border-radius:6px;padding:8px;">
    <div class="small" style="margin-bottom:6px;font-weight:600;">Generate Sections</div>
    <div class="row" style="gap:8px;flex-wrap:wrap;">
      <label class="small">Spacing (ft) <input id="xs-spacing" type="number" value="50" min="1" style="width:52px;"/></label>
      <label class="small">Half-width (ft) <input id="xs-halfwidth" type="number" value="100" min="1" style="width:52px;"/></label>
    </div>
    <div style="margin-top:6px;">
      <button id="xs-gen-sections" class="btn btn-sm">Generate</button>
    </div>
  </div>

  <!-- Surfaces -->
  <div style="border:1px solid #1e293b;border-radius:4px;padding:6px;">
    <div class="small" style="font-weight:600;margin-bottom:4px;">Surfaces</div>
    <div id="xs-surfaces-list"></div>
  </div>

  <!-- Section table -->
  <div id="xs-table-wrap" style="flex:1;min-height:0;overflow-y:auto;border:1px solid #1e293b;border-radius:4px;">
    <table style="width:100%;border-collapse:collapse;">
      <thead><tr>
        <th class="small" style="text-align:left;padding:2px 6px;color:#64748b;">Station</th>
        <th class="small" style="text-align:left;padding:2px 6px;color:#64748b;">Length</th>
        <th class="small" style="text-align:left;padding:2px 6px;color:#64748b;">½-Width</th>
      </tr></thead>
      <tbody id="xs-table-body"></tbody>
    </table>
  </div>

  <!-- Actions -->
  <div class="row" style="gap:6px;">
    <button id="xs-export-csv" class="btn btn-sm">CSV Export</button>
    <button id="xs-delete-sel" class="btn btn-sm btn-danger">Delete Selected</button>
  </div>

  <!-- Profile chart -->
  <div style="border:1px solid #1e293b;border-radius:4px;padding:4px;position:relative;min-height:240px;">
    <div style="display:flex;align-items:center;gap:4px;margin-bottom:2px;">
      <span class="small" style="font-weight:600;flex:1;">Profile</span>
      <label style="display:flex;align-items:center;gap:3px;font-size:10px;color:#64748b;">VE
        <input id="xs-ve-select" type="number" min="0.01" step="0.01" value="${verticalExaggeration.toFixed(2)}"
          title="Vertical exaggeration (Ctrl+scroll to adjust)"
          style="width:52px;background:#0f172a;border:1px solid #334155;color:#e2e8f0;border-radius:3px;font-size:10px;padding:1px 4px;"/>
      </label>
      <button id="xs-nav-prev" class="btn btn-sm" title="Previous section" style="padding:2px 7px;">&#9664;</button>
      <button id="xs-nav-next" class="btn btn-sm" title="Next section" style="padding:2px 7px;">&#9654;</button>
      <button id="xs-zoom-reset" class="btn btn-sm" title="Reset zoom" style="display:none;padding:2px 7px;">&#8635;</button>
    </div>
    <div id="xs-chart" style="position:relative;user-select:none;"></div>
  </div>

</div>`;

    const chartEl = container.querySelector("#xs-chart");
    initChartInteraction(chartEl);
    if (chartResizeObserver) chartResizeObserver.disconnect();
    chartResizeObserver = new ResizeObserver(() => renderChart());
    chartResizeObserver.observe(chartEl);

    // Hover marker on map
    hoverMarker = L.circleMarker([0, 0], {
      radius: 6, color: "#f97316", fillColor: "#f97316", fillOpacity: 1, opacity: 0, interactive: false,
    }).addTo(map);

    document.addEventListener("hydrotools:marina-changed", onMarinaChanged);
    bindControls();
    loadCatalog().then(() => {
      loadAvailSurfaces();
      if (catalog.length) activeSetId = catalog[0].id;
      redrawAllSections();
      renderPanel();
    });
  }

  function onMarinaChanged(e) {
    const code = String(e.detail?.marinaId || "").trim().toUpperCase();
    if (code === activeMarinaCode) return;
    activeMarinaCode = code;
    catalog = [];
    activeSetId = null;
    profileCache.clear();
    availSurfaces = [];
    loadCatalog().then(() => {
      loadAvailSurfaces();
      if (catalog.length) activeSetId = catalog[0].id;
      redrawAllSections();
      renderPanel();
    });
  }

  function bindControls() {
    // Set selector
    container.querySelector("#xs-set-select").onchange = (e) => {
      activeSetId = e.target.value || null;
      if (!activeSetId && e.target.value === "") {
        // "New set" — create immediately
        const s = newSet();
        catalog.push(s);
        activeSetId = s.id;
        saveCatalog();
      }
      selectedIds.clear();
      redrawAllSections();
      renderPanel();
    };

    container.querySelector("#xs-set-new").onclick = () => {
      const s = newSet();
      catalog.push(s);
      activeSetId = s.id;
      selectedIds.clear();
      redrawAllSections();
      saveCatalog();
      renderPanel();
    };

    container.querySelector("#xs-set-delete").onclick = () => {
      if (!activeSetId) return;
      if (!confirm("Delete this section set?")) return;
      catalog = catalog.filter(s => s.id !== activeSetId);
      activeSetId = catalog[0]?.id ?? null;
      selectedIds.clear();
      redrawAllSections();
      saveCatalog();
      renderPanel();
    };

    container.querySelector("#xs-set-rename").onclick = () => {
      const set = activeSet();
      const nameEl = container.querySelector("#xs-setname");
      if (!set || !nameEl) return;
      set.name = nameEl.value.trim() || set.name;
      saveCatalog();
      renderPanel();
    };

    container.querySelector("#xs-draw-single").onclick = () => {
      if (drawMode === "single") { stopDraw(); return; }
      startDraw("single");
      container.querySelector("#xs-stop-draw").style.display = "";
    };

    container.querySelector("#xs-draw-cl").onclick = () => {
      if (drawMode === "centerline") { stopDraw(); return; }
      startDraw("centerline");
      container.querySelector("#xs-stop-draw").style.display = "";
    };

    container.querySelector("#xs-stop-draw").onclick = () => {
      stopDraw();
      container.querySelector("#xs-stop-draw").style.display = "none";
    };

    container.querySelector("#xs-gen-sections").onclick = () => {
      const set = activeSet();
      if (!set) return;
      const spEl = container.querySelector("#xs-spacing");
      const hwEl = container.querySelector("#xs-halfwidth");
      if (spEl) set.spacing = Math.max(1, Number(spEl.value) || 50);
      if (hwEl) set.defaultHalfWidth = Math.max(1, Number(hwEl.value) || 100);
      generateFromCenterline();
    };

    container.querySelector("#xs-export-csv").onclick = exportCSV;

    container.querySelector("#xs-nav-prev").onclick = () => selectAdjacentSection(-1);
    container.querySelector("#xs-nav-next").onclick = () => selectAdjacentSection(1);
    container.querySelector("#xs-zoom-reset").onclick = () => { chartZoom = null; renderChart(); };
    container.querySelector("#xs-ve-select").onchange = (e) => {
      const v = parseFloat(e.target.value);
      if (!Number.isFinite(v) || v < 0.01) return;
      verticalExaggeration = Math.round(v * 100) / 100;
      chartZoom = null;
      renderChart();
    };

    container.querySelector("#xs-delete-sel").onclick = () => {
      const set = activeSet();
      if (!set || !selectedIds.size) return;
      set.sections = set.sections.filter(s => !selectedIds.has(s.id));
      for (const id of selectedIds) {
        removeSectionFromMap(id);
        for (const rp of (set.surfaceRasterPaths || [])) profileCache.delete(`${rp}::${id}`);
      }
      selectedIds.clear();
      saveCatalog();
      renderPanel();
      renderChart();
    };
  }

  // ── Chart interaction (zoom/pan) ── attached once, reads lastChartExtents ──
  function initChartInteraction(chartEl) {
    // Wheel: Ctrl = adjust vertical exaggeration; plain = zoom both axes
    chartEl.addEventListener("wheel", (e) => {
      e.preventDefault();
      const ext = lastChartExtents;
      if (!ext) return;

      if (e.ctrlKey) {
        // scroll down (deltaY>0) = decrease VE (flatten); scroll up = increase
        const dir = e.deltaY > 0 ? -1 : 1;
        verticalExaggeration = Math.round(Math.max(0.01, verticalExaggeration + dir * 0.01) * 100) / 100;
        const veInput = container?.querySelector?.("#xs-ve-select");
        if (veInput) veInput.value = verticalExaggeration.toFixed(2);
        // preserve X zoom — VE owns Y, so no chartZoom reset needed
        renderChart();
        return;
      }

      const rect = chartEl.getBoundingClientRect();
      const svgX = e.clientX - rect.left;
      const svgY = e.clientY - rect.top;
      const { vxMin, vxMax, vyMin, vyMax, rawYMin, rawYMax, ML, MT, cW, cH } = ext;
      const datX = vxMin + ((svgX - ML) / cW) * (vxMax - vxMin);
      // Display Y → raw Y: rawY = yCenter + (displayY - yCenter) * VE
      const displayDatY = vyMin + (1 - (svgY - MT) / cH) * (vyMax - vyMin);
      const rawYCenter = (rawYMin + rawYMax) / 2;
      const rawDatY = rawYCenter + (displayDatY - rawYCenter) * verticalExaggeration;
      const factor = e.deltaY > 0 ? 1.25 : 0.8;
      chartZoom = {
        xMin: datX - (datX - vxMin) * factor,
        xMax: datX + (vxMax - datX) * factor,
        yMin: rawDatY - (rawDatY - rawYMin) * factor,
        yMax: rawDatY + (rawYMax - rawDatY) * factor,
      };
      renderChart();
    }, { passive: false });

    // Drag: pan the view
    chartEl.addEventListener("mousedown", (e) => {
      if (e.button !== 0) return;
      const ext = lastChartExtents;
      if (!ext) return;
      e.preventDefault();
      const startX = e.clientX, startY = e.clientY;
      const startZoom = chartZoom
        ? { ...chartZoom }
        : { xMin: ext.vxMin, xMax: ext.vxMax, yMin: ext.rawYMin, yMax: ext.rawYMax };
      const { cW, cH, vyMin, vyMax } = ext;
      const xRange = startZoom.xMax - startZoom.xMin;
      const displayYRange = vyMax - vyMin;

      function onMove(me) {
        const dx = (me.clientX - startX) / cW * xRange;
        // Display Y pan → raw Y: multiply by VE to convert display-ft to raw-ft
        const displayDy = (me.clientY - startY) / cH * displayYRange;
        const rawDy = displayDy * verticalExaggeration;
        chartZoom = {
          xMin: startZoom.xMin - dx,
          xMax: startZoom.xMax - dx,
          yMin: startZoom.yMin + rawDy,
          yMax: startZoom.yMax + rawDy,
        };
        renderChart();
      }
      function onUp() {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      }
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    });

    // Double-click: reset zoom
    chartEl.addEventListener("dblclick", () => {
      chartZoom = null;
      renderChart();
    });
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────────
  function destroy() {
    document.removeEventListener("hydrotools:marina-changed", onMarinaChanged);
    stopDraw(); // also removes cursor style
    if (chartResizeObserver) { chartResizeObserver.disconnect(); chartResizeObserver = null; }
    root.remove();
    // Remove all section/centerline layers that were added directly to map
    for (const { line, labelMarker } of layersBySectionId.values()) {
      line.remove(); labelMarker.remove();
    }
    layersBySectionId.clear();
    if (centerlineLayer) { centerlineLayer.remove(); centerlineLayer = null; }
    if (hoverMarker) { hoverMarker.remove(); hoverMarker = null; }
    container = null;
  }

  function snapshot() {
    return { catalog, activeSetId };
  }

  function restore(data) {
    if (!data) return;
    if (Array.isArray(data.catalog)) catalog = data.catalog;
    if (data.activeSetId) activeSetId = data.activeSetId;
    redrawAllSections();
    renderPanel();
  }

  return { mount, destroy, snapshot, restore };
}
